// src/pages/Customers/CustomersIndex.tsx
/**
 * CustomersIndex
 * -----------------------------------------------------------------------------
 * Mirrors the (former Vendors →) Contacts page — same layout and fields —
 * with an extra Filters bar (by Tag and State) in addition to full-text search.
 *
 * Storage keys
 * - Data:      customers:data:v1
 * - Table:     tbl:customers:v1
 * - Tag opts:  opts:customerTags:v1
 *
 * Assumptions
 * - <DataTable/> supports { columns, rows, filename, storageKey }.
 * - <Drawer/>, <AppShell/>, <MultiSelect/>, notifications, audit utilities exist.
 */

import { useEffect, useMemo, useState } from 'react'
import { Plus, Pencil, Undo2, Trash2 } from 'lucide-react'
import AppShell from '@/components/AppShell'
import Drawer from '@/components/Drawer'
import DataTable, { Col } from '@/components/DataTable'
import MultiSelect from '@/components/MultiSelect'
import { useNotifications } from '@/lib/notifications'
import { logAudit } from '@/lib/audit'
import { get as getLS } from '@/lib/storage'

type Customer = {
  id: string
  name: string
  emails: string[]        // multiple allowed
  phone?: string
  taxId?: string
  address1?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  loaderModel?: string
  tags: string[]
  flags?: {
    groupOnStatement?: boolean
    onlyShowNetUnits?: boolean
    useAIForTickets?: boolean
    separateEachStand?: boolean
    separateEachJob?: boolean
  }
  deleted?: boolean
  createdAt?: string
  updatedAt?: string
}

const TBL_KEY = 'tbl:customers:v1'
const DATA_KEY = 'customers:data:v1'
const TAGS_KEY = 'opts:customerTags:v1'

const TABS = ['My Customers', 'Customer Users', 'Deleted customers'] as const
type Tab = typeof TABS[number]

/* --------------------------------- helpers -------------------------------- */

function splitEmails(value: string): string[] {
  return value
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function uniqueEmails(customers: Customer[]): { email: string; customers: Customer[] }[] {
  const map = new Map<string, Customer[]>()
  customers.forEach((c) => {
    (c.emails || []).forEach((e) => {
      const key = (e || '').toLowerCase()
      if (!key) return
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(c)
    })
  })
  return Array.from(map.entries()).map(([email, list]) => ({ email, customers: list }))
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid">
      <label className="text-sm">{label}</label>
      {children}
    </div>
  )
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="accent-[var(--brand-600)]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  )
}

/* ---------------------------------- page ---------------------------------- */

export default function CustomersIndex() {
  const { push } = useNotifications()

  const [tab, setTab] = useState<Tab>('My Customers')
  const [q, setQ] = useState('')

  // Filters (extra vs Contacts page)
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [stateFilter, setStateFilter] = useState<string>('')

  const [customers, setCustomers] = useState<Customer[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Customer | null>(null)

  // ---- load/persist ---------------------------------------------------------
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DATA_KEY) || '[]'
      const list = JSON.parse(raw) as Customer[]
      setCustomers(Array.isArray(list) ? list : [])
    } catch {
      setCustomers([])
    }
  }, [])

  function persist(next: Customer[]) {
    localStorage.setItem(DATA_KEY, JSON.stringify(next))
    setCustomers(next)
  }

  // ---- derived --------------------------------------------------------------
  const active = customers.filter((c) => !c.deleted)
  const removed = customers.filter((c) => !!c.deleted)

  const counts = {
    'My Customers': active.length,
    'Customer Users': uniqueEmails(active).length,
    'Deleted customers': removed.length,
  } as Record<Tab, number>

  // filter option pools
  const allTags = useMemo(() => {
    const s = new Set<string>()
    customers.forEach((c) => (c.tags || []).forEach((t) => s.add(t)))
    return Array.from(s).sort((a, b) => a.localeCompare(b))
  }, [customers])

  const allStates = useMemo(() => {
    const s = new Set<string>()
    customers.forEach((c) => c.state && s.add(c.state))
    return Array.from(s).sort((a, b) => a.localeCompare(b))
  }, [customers])

  const filtered = useMemo(() => {
    const hay = (c: Customer) =>
      [
        c.name,
        c.emails?.join(' '),
        c.phone,
        c.city,
        c.state,
        c.zip,
        c.tags?.join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

    const query = q.trim().toLowerCase()
    const base = tab === 'My Customers' ? active : tab === 'Deleted customers' ? removed : active

    return base.filter((c) => {
      // text query
      if (query && !hay(c).includes(query)) return false
      // state filter
      if (stateFilter && (c.state || '') !== stateFilter) return false
      // tag filter (must contain *all* selected tags)
      if (tagFilter.length && !tagFilter.every((t) => (c.tags || []).includes(t))) return false
      return true
    })
  }, [active, removed, q, tab, stateFilter, tagFilter])

  // ---- create/edit ----------------------------------------------------------
  function openCreate() {
    setEditing({
      id: crypto.randomUUID(),
      name: '',
      emails: [],
      phone: '',
      taxId: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      loaderModel: '',
      tags: [],
      flags: {
        groupOnStatement: false,
        onlyShowNetUnits: false,
        useAIForTickets: false,
        separateEachStand: false,
        separateEachJob: false,
      },
      deleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
    setDrawerOpen(true)
  }
  function openEdit(c: Customer) {
    setEditing({ ...c })
    setDrawerOpen(true)
  }

  function saveEditing() {
    if (!editing) return
    const name = editing.name?.trim()
    if (!name) {
      push({ title: 'Customer name is required', level: 'warning' })
      return
    }
    const list = [...customers]
    const idx = list.findIndex((c) => c.id === editing.id)
    const now = new Date().toISOString()
    const record: Customer = { ...editing, updatedAt: now }

    if (idx >= 0) list[idx] = record
    else list.unshift(record)

    persist(list)
    setDrawerOpen(false)
    logAudit({ action: idx >= 0 ? 'customer-update' : 'customer-create', details: record }, getLS('role', 'Admin'))
    push({ title: idx >= 0 ? 'Customer updated' : 'Customer created', body: record.name })
  }

  function softDelete(c: Customer) {
    const list = customers.map((x) => (x.id === c.id ? { ...x, deleted: true, updatedAt: new Date().toISOString() } : x))
    persist(list)
    logAudit({ action: 'customer-delete', details: { id: c.id, name: c.name } }, getLS('role', 'Admin'))
    push({ title: 'Customer deleted', body: c.name })
  }
  function restore(c: Customer) {
    const list = customers.map((x) => (x.id === c.id ? { ...x, deleted: false, updatedAt: new Date().toISOString() } : x))
    persist(list)
    logAudit({ action: 'customer-restore', details: { id: c.id, name: c.name } }, getLS('role', 'Admin'))
    push({ title: 'Customer restored', body: c.name })
  }

  // ---- tables ---------------------------------------------------------------
  const cols: Col[] = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'tags', label: 'Tags' },
    { key: 'actions', label: '' },
  ]
  const rows = filtered.map((c) => ({
    name: <div className="truncate">{c.name}</div>,
    email: c.emails?.length ? c.emails.join(', ') : <span className="text-[var(--muted)]">–</span>,
    phone: c.phone || <span className="text-[var(--muted)]">–</span>,
    tags: c.tags?.length ? (
      <div className="flex flex-wrap gap-1">
        {c.tags.map((t) => (
          <span key={t} className="badge">
            {t}
          </span>
        ))}
      </div>
    ) : (
      <span className="text-[var(--muted)]">–</span>
    ),
    actions: (
      <div className="flex justify-end gap-2">
        {!c.deleted ? (
          <>
            <button className="btn btn-secondary" onClick={() => openEdit(c)} title="Edit">
              <Pencil className="w-4 h-4" />
            </button>
            <button className="btn btn-secondary" onClick={() => softDelete(c)} title="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button className="btn btn-secondary" onClick={() => restore(c)} title="Restore">
            <Undo2 className="w-4 h-4" />
          </button>
        )}
      </div>
    ),
  }))

  const usersCols: Col[] = [
    { key: 'email', label: 'Email' },
    { key: 'customers', label: 'Customers' },
  ]
  const usersRows = uniqueEmails(active).map((u) => ({
    email: u.email,
    customers: (
      <div className="flex flex-wrap gap-1">
        {u.customers.map((c) => (
          <span key={c.id} className="badge">
            {c.name}
          </span>
        ))}
      </div>
    ),
  }))

  /* -------------------------------- render -------------------------------- */

  return (
    <AppShell title="Customers">
      {/* Tabs + action */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-4">
          {TABS.map((t) => (
            <button
              key={t}
              className={`pb-2 border-b-2 -mb-px ${
                tab === t ? 'border-[var(--brand-600)] text-[var(--text)]' : 'border-transparent text-[var(--muted)]'
              }`}
              onClick={() => setTab(t)}
            >
              {t} <span className="text-xs text-[var(--muted)]">({counts[t]})</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button className="btn" onClick={openCreate}>
            <Plus className="w-4 h-4" /> Create Customer
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="card mb-3">
        <div className="card-body grid md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-sm">Search</label>
            <input className="input w-full" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>

          <div>
            <label className="text-sm">Filter by Tag</label>
            <MultiSelect
              label=""
              placeholder="Select tags…"
              options={allTags}
              value={tagFilter}
              storageKey={TAGS_KEY} // also keeps suggestions growing
              onChange={setTagFilter}
            />
          </div>

          <div>
            <label className="text-sm">Filter by State</label>
            <select className="select w-full" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              <option value="">All states</option>
              {allStates.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        {(tagFilter.length || stateFilter) && (
          <div className="px-4 pb-3 -mt-2 text-sm">
            <span className="text-[var(--muted)] mr-2">Active filters:</span>
            {stateFilter && <span className="badge mr-1">State: {stateFilter}</span>}
            {tagFilter.map((t) => (
              <span key={t} className="badge mr-1">
                Tag: {t}
              </span>
            ))}
            <button
              className="btn btn-secondary ml-2"
              onClick={() => {
                setTagFilter([])
                setStateFilter('')
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Tables */}
      {tab === 'Customer Users' ? (
        <DataTable columns={usersCols} rows={usersRows} filename="customer-users.csv" storageKey={`${TBL_KEY}:users`} />
      ) : (
        <DataTable columns={cols} rows={rows} filename="customers.csv" storageKey={TBL_KEY} />
      )}

      {/* Drawer: Create/Edit */}
      <Drawer
        open={drawerOpen}
        title={editing?.id && customers.some((c) => c.id === editing.id) ? 'Edit Customer' : 'Create Customer'}
        onClose={() => setDrawerOpen(false)}
      >
        <div className="drawer-panel p-4 grid gap-4">
          {/* Customer Details */}
          <div className="card">
            <div className="card-header">Customer Details</div>
            <div className="card-body grid md:grid-cols-2 gap-3">
              <Labeled label="Full Name*">
                <input
                  className="input"
                  value={editing?.name || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, name: e.target.value } : s))}
                  placeholder="Full Name"
                />
              </Labeled>

              <Labeled label="Email (enter multiple separated by comma)">
                <input
                  className="input"
                  value={editing?.emails?.join(', ') || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, emails: splitEmails(e.target.value) } : s))}
                  placeholder="name@example.com, another@email.com"
                />
              </Labeled>

              <Labeled label="Phone">
                <input
                  className="input"
                  value={editing?.phone || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, phone: e.target.value } : s))}
                  placeholder="(###) ###-####"
                />
              </Labeled>

              <Labeled label="Tax ID / SSN">
                <input
                  className="input"
                  value={editing?.taxId || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, taxId: e.target.value } : s))}
                />
              </Labeled>

              <Labeled label="Street Address">
                <input
                  className="input"
                  value={editing?.address1 || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, address1: e.target.value } : s))}
                />
              </Labeled>

              <Labeled label="Street Address 2">
                <input
                  className="input"
                  value={editing?.address2 || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, address2: e.target.value } : s))}
                />
              </Labeled>

              <Labeled label="City">
                <input
                  className="input"
                  value={editing?.city || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, city: e.target.value } : s))}
                />
              </Labeled>

              <Labeled label="State">
                <input
                  className="input"
                  value={editing?.state || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, state: e.target.value } : s))}
                />
              </Labeled>

              <Labeled label="Zip">
                <input
                  className="input"
                  value={editing?.zip || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, zip: e.target.value } : s))}
                />
              </Labeled>

              <Labeled label="Loader model">
                <input
                  className="input"
                  value={editing?.loaderModel || ''}
                  onChange={(e) => setEditing((s) => (s ? { ...s, loaderModel: e.target.value } : s))}
                />
              </Labeled>

              <div className="md:col-span-2">
                <Labeled label="Tags">
                  <MultiSelect
                    label=""
                    placeholder="Add tags…"
                    options={[]} // learned via storageKey
                    value={editing?.tags || []}
                    storageKey={TAGS_KEY}
                    onChange={(vals) => setEditing((s) => (s ? { ...s, tags: vals } : s))}
                  />
                </Labeled>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="card">
            <div className="card-body grid md:grid-cols-2 gap-3">
              <Toggle
                label="Group Transactions on Statement"
                checked={!!editing?.flags?.groupOnStatement}
                onChange={(v) => setEditing((s) => (s ? { ...s, flags: { ...s.flags, groupOnStatement: v } } : s))}
              />
              <Toggle
                label="Only Show Net Units On Statements"
                checked={!!editing?.flags?.onlyShowNetUnits}
                onChange={(v) => setEditing((s) => (s ? { ...s, flags: { ...s.flags, onlyShowNetUnits: v } } : s))}
              />
              <Toggle
                label="Use AI to process uploaded scale tickets"
                checked={!!editing?.flags?.useAIForTickets}
                onChange={(v) => setEditing((s) => (s ? { ...s, flags: { ...s.flags, useAIForTickets: v } } : s))}
              />
              <Toggle
                label="Settle each stand on a separate statement"
                checked={!!editing?.flags?.separateEachStand}
                onChange={(v) => setEditing((s) => (s ? { ...s, flags: { ...s.flags, separateEachStand: v } } : s))}
              />
              <Toggle
                label="Settle each job on a separate statement"
                checked={!!editing?.flags?.separateEachJob}
                onChange={(v) => setEditing((s) => (s ? { ...s, flags: { ...s.flags, separateEachJob: v } } : s))}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button className="btn" onClick={saveEditing}>
              Save
            </button>
            <button className="btn btn-secondary" onClick={() => setDrawerOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Drawer>
    </AppShell>
  )
}
